

// src/pages/VehicleV2/CloseHireVehicleListing.jsx
// Matches closehirevehicle.aspx + production listing template
//
// GET  /api/closeHireVehicle/dropdowns
// GET  /api/closeHireVehicle/list
// POST /api/closeHireVehicle/toggle  { vehicleId }

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaArrowLeft,
  FaSyncAlt,
  FaEye,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/closeHireVehicle`;

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const a = localStorage.getItem(key);
  if (a !== null && String(a).trim() !== "") return a;
  const b = sessionStorage.getItem(key);
  if (b !== null && String(b).trim() !== "") return b;
  return fallback;
};

const defaultFilters = {
  store: "Select",
  vehicleTypeIndex: "Select",
  vehicleCode: "",
  vehicleName: "",
  vehicleNo: "",
};

const CloseHireVehicleListing = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = getLS("finYearId") || getLS("finyearid");
  const finYear = getLS("finYear") || getLS("finyear");
  const branchId =
    getLS("branchId") || getLS("Branchid") || getLS("branchid") || "0";
  const branchType =
    getLS("branchType") || getLS("branchtype") || getLS("BranchType") || "HO";
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId") || "0";
  const loginUserId =
    sessionStorage.getItem("loginUserId") ||
    sessionStorage.getItem("loginuserid") ||
    userId ||
    "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [filters, setFilters] = useState(defaultFilters);
  const [opts, setOpts] = useState({
    stores: [{ label: "Select", value: "Select" }],
    vehicleTypes: [{ label: "Select", value: "Select" }],
  });
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const isBusy = pageLoading || gridLoading || actionLoading;

  const fieldClass = useMemo(
    () =>
      `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  // ASPX FillGrid: usp_closehirevehicle branchType, branchId|'', code, name, no, userId, vehicleTypeId
  // Store is NOT sent to the list SP (only autocomplete / filter UI).
  const buildListParams = useCallback(
    (override = {}) => ({
      branchId,
      branchType,
      loginUserId,
      vehicleCode: toStr(override.vehicleCode ?? filters.vehicleCode),
      vehicleName: toStr(override.vehicleName ?? filters.vehicleName),
      vehicleNo: toStr(override.vehicleNo ?? filters.vehicleNo),
      vehicleTypeIndex:
        toStr(override.vehicleTypeIndex ?? filters.vehicleTypeIndex) ||
        "Select",
    }),
    [branchId, branchType, loginUserId, filters]
  );

  const mappedRows = useMemo(() => {
    return toList(rows).map((r, idx) => {
      const statusFromSp = toStr(r.Status || r.status || r.fld_status);
      let status = statusFromSp;

      if (!status) {
        const closeHire = r.fld_closehire ?? r.fld_CloseHire;
        const isRemove = r.fld_isremove ?? r.fld_isRemove ?? r.fld_IsRemove;
        const closed =
          closeHire === 1 ||
          closeHire === "1" ||
          closeHire === true ||
          isRemove === 1 ||
          isRemove === "1" ||
          isRemove === true;
        status = closed ? "Closed" : "Open";
      }

      return {
        __rowId: idx + 1,
        vehicleId: r.fld_vehicleid || r.vehicleId || r.id || "",
        branch: r.fld_BranchName || r.fld_branchname || r.branch || "",
        vehicleMainType:
          r.VehicleType || r.fld_vehicletypename || r.vehicleMainType || "",
        vehicleCode: r.fld_vehicle_code || r.vehicleCode || "",
        vehicleName: r.fld_vehicle_name || r.vehicleName || "",
        vehicleNumber: r.fld_vehicle_regno || r.vehicleNumber || "",
        status,
      };
    });
  }, [rows]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(mappedRows.length / pageSize)),
    [mappedRows.length]
  );

  const paginatedRows = useMemo(
    () => mappedRows.slice((page - 1) * pageSize, page * pageSize),
    [mappedRows, page]
  );

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const loadDropdowns = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/dropdowns`, {
      params: { branchId, branchType, loginUserId },
      headers: commonHeaders,
    });

    if (!data?.success) {
      throw new Error(data?.message || "Failed to load dropdowns");
    }

    const stores = [
      { label: "Select", value: "Select" },
      ...toList(data.stores)
        .map((x) => ({
          label: String(x.label ?? x.fld_storename ?? x.name ?? ""),
          value: String(x.value ?? x.fld_storeid ?? x.id ?? ""),
        }))
        .filter((x) => x.value && x.value !== "Select"),
    ];

    const vehicleTypes = [
      { label: "Select", value: "Select" },
      ...toList(data.vehicleTypes)
        .map((x) => ({
          label: String(x.label ?? x.fld_vehicletypename ?? x.name ?? ""),
          value: String(x.value ?? x.fld_vehicletypeindex ?? x.id ?? ""),
        }))
        .filter((x) => x.value && x.value !== "Select"),
    ];

    setOpts({ stores, vehicleTypes });
  }, [branchId, branchType, loginUserId, commonHeaders]);

  const loadList = useCallback(
    async ({
      silent = false,
      overrideFilters = null,
      resetPage = true,
    } = {}) => {
      if (!toStr(loginUserId) || loginUserId === "0") {
        if (!silent) toast.error("loginUserId missing");
        return;
      }

      try {
        setGridLoading(true);
        setHasSearched(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(overrideFilters || {}),
          headers: commonHeaders,
        });

        if (!data?.success) {
          setRows([]);
          if (resetPage) setPage(1);
          if (!silent) toast.warn(data?.message || "No Records found");
          return;
        }

        const listRows = toList(data.list || data.rows || data.data);
        setRows(listRows);
        if (resetPage) setPage(1);

        if (!silent) {
          if (!listRows.length) toast.info("No Records found");
          else toast.success(`Found ${listRows.length} record(s)`);
        }
      } catch (err) {
        console.error(err);
        setRows([]);
        if (resetPage) setPage(1);
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load list"
        );
      } finally {
        setGridLoading(false);
      }
    },
    [loginUserId, commonHeaders, buildListParams]
  );

  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);
      await loadDropdowns();
      await loadList({ silent: true });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Initialization failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [loadDropdowns, loadList]);

  const handleSearch = useCallback(async () => {
    await loadList({ silent: false, resetPage: true });
  }, [loadList]);

  const handleViewAll = useCallback(async () => {
    const resetObj = { ...defaultFilters };
    setFilters(resetObj);
    await loadList({
      silent: false,
      overrideFilters: resetObj,
      resetPage: true,
    });
  }, [loadList]);

  const handleClearFilters = useCallback(() => {
    setFilters(defaultFilters);
    setPage(1);
  }, []);

  const handleRefresh = useCallback(async () => {
    await loadList({ silent: false, resetPage: false });
  }, [loadList]);

  const handleToggleStatus = useCallback(
    async (vehicleId) => {
      if (!vehicleId) {
        toast.error("vehicleId missing");
        return;
      }

      try {
        setActionLoading(true);

        const { data } = await axios.post(
          `${API_BASE}/toggle`,
          { vehicleId },
          { headers: commonHeaders }
        );

        if (!data?.success) {
          toast.warn(
            data?.message ||
              "Vehicle cannot be close as the Bill genaration is not done"
          );
          return;
        }

        toast.success(data?.message || "Status updated");
        await loadList({ silent: true, resetPage: false });
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message ||
            err.message ||
            "Vehicle cannot be close as the Bill genaration is not done"
        );
      } finally {
        setActionLoading(false);
      }
    },
    [commonHeaders, loadList]
  );

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isClosedStatus = (status) => {
    const s = toStr(status).toLowerCase();
    return s.includes("close") && !s.includes("open");
  };

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">CLOSE HIRE VEHICLE</span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Close Hire Vehicle Listing
              </h2>
              <p
                className={`text-sm mt-1 ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Branch ID: {branchId || "-"} • User ID: {loginUserId || "-"} •
                Records: {mappedRows.length}
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => setShowFilters((s) => !s)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                type="button"
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft />
                Back
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`p-6 rounded-lg mb-6 ${
                isDark ? "bg-gray-800/50" : "bg-blue-50"
              }`}
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? "text-white" : "text-blue-900"
                  }`}
                >
                  Search Criteria
                </h3>

                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    type="button"
                    onClick={handleViewAll}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <FaEye />
                    View All
                  </button>

                  <button
                    type="button"
                    onClick={handleSearch}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {gridLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    value={filters.vehicleName}
                    onChange={(e) => setField("vehicleName", e.target.value)}
                    className={fieldClass}
                    placeholder="Vehicle Name"
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Number</label>
                  <input
                    type="text"
                    value={filters.vehicleNo}
                    onChange={(e) => setField("vehicleNo", e.target.value)}
                    className={fieldClass}
                    placeholder="Vehicle Number"
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    value={filters.vehicleCode}
                    onChange={(e) => setField("vehicleCode", e.target.value)}
                    className={fieldClass}
                    placeholder="Vehicle Code"
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    value={filters.store}
                    onChange={(e) => setField("store", e.target.value)}
                    className={fieldClass}
                    disabled={isBusy}
                  >
                    {opts.stores.map((o, i) => (
                      <option key={`${o.value}-${i}`} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    value={filters.vehicleTypeIndex}
                    onChange={(e) =>
                      setField("vehicleTypeIndex", e.target.value)
                    }
                    className={fieldClass}
                    disabled={isBusy}
                  >
                    {opts.vehicleTypes.map((o, i) => (
                      <option key={`${o.value}-${i}`} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || gridLoading ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Loading...
                </div>
              </div>
            ) : mappedRows.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {hasSearched
                    ? "No Records found"
                    : "Please search to view records"}
                </div>
              </div>
            ) : (
              <>
                <table
                  className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}
                >
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        S.No
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Branch
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Main Type
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Code
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Name
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Number
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    className={`${
                      isDark ? "divide-gray-700" : "divide-gray-200"
                    } divide-y`}
                  >
                    {paginatedRows.map((row, idx) => {
                      const sno = (page - 1) * pageSize + idx;
                      const closed = isClosedStatus(row.status);
                      return (
                        <tr
                          key={`${row.vehicleId || row.__rowId}-${sno}`}
                          className={`${
                            sno % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                              ? "bg-gray-750"
                              : "bg-gray-50"
                          } transition-colors ${
                            isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                          }`}
                        >
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {sno + 1}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.branch || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleMainType || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm font-medium ${
                              isDark ? "text-blue-400" : "text-blue-600"
                            }`}
                          >
                            {row.vehicleCode || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm font-medium ${
                              isDark ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleName || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleNumber || "-"}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleToggleStatus(row.vehicleId)}
                              disabled={isBusy || !row.vehicleId}
                              className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                closed
                                  ? isDark
                                    ? "bg-red-900/40 text-red-200 hover:bg-red-900/60"
                                    : "bg-red-100 text-red-800 hover:bg-red-200"
                                  : isDark
                                  ? "bg-green-900/40 text-green-200 hover:bg-green-900/60"
                                  : "bg-green-100 text-green-800 hover:bg-green-200"
                              } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                              title="Click to close / open hire vehicle"
                            >
                              {row.status || "Open"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div
                    className={`text-sm ${
                      isDark ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Records:{" "}
                    <span className="font-semibold">{mappedRows.length}</span>
                    {actionLoading ? " • Updating status..." : ""}
                  </div>
                </div>
              </>
            )}
          </div>

          {mappedRows.length > 0 && totalPages > 1 && !isBusy && (
            <div className="mt-6 flex justify-center">
              <nav className="flex gap-1 flex-wrap items-center">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-3 py-1 border rounded text-sm ${
                    isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  } disabled:opacity-50`}
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    type="button"
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-3 py-1 border rounded text-sm ${
                      page === i + 1
                        ? `${buttonClass} text-white`
                        : isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={page === totalPages}
                  className={`px-3 py-1 border rounded text-sm ${
                    isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  } disabled:opacity-50`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default CloseHireVehicleListing;